// FeaturesSection.tsx
// Copy of the "Compare all features" section from marketing pricing page.
// Same visual styling: gradient border for Vela, gray for others, dark circle icons.
// Flat list per category (no plan columns) - green tick + feature name only.

import React, { useState, ReactElement } from "react";
import { useTranslation } from "react-i18next";

const CATEGORIES = [
  "vela","payroll","accounting","invoicing",
  "expenses","workforce","reporting","compliance","platform"
] as const;

type CategoryKey = typeof CATEGORIES[number];

const CATEGORY_ICONS: Record<CategoryKey, ReactElement> = {
  vela: <svg width={20} height={20} viewBox="0 0 24 24" fill="#53E875"><path d="M12 2l2.4 7.4H22l-6.2 4.5L18.2 22 12 17.3 5.8 22l2.4-8.1L2 9.4h7.6z"/></svg>,
  payroll: <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#53E875" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  accounting: <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#53E875" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>,
  invoicing: <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#53E875" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h6"/></svg>,
  expenses: <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#53E875" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  workforce: <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#53E875" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  reporting: <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#53E875" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  compliance: <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#53E875" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  platform: <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#53E875" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
};

export default function FeaturesSection() {
  const { t } = useTranslation();
  const [openCats, setOpenCats] = useState<Record<string, boolean>>(
    () => Object.fromEntries(CATEGORIES.map(c => [c, true]))
  );

  return (
    <section style={{ padding: "80px 32px", maxWidth: 900, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <h2 style={{ fontSize: 40, fontWeight: 300, color: "#0E1A1A", margin: 0, letterSpacing: "-0.02em" }}>
          {t("features.title")}
        </h2>
        <p style={{ fontSize: 16, color: "#374151", marginTop: 12 }}>{t("features.subtitle")}</p>
      </div>

      {CATEGORIES.map((cat) => {
        const isVela = cat === "vela";
        const isOpen = openCats[cat];
        const label = t(`features.categories.${cat}.label`);
        const rows = (t(`features.categories.${cat}.rows`, { returnObjects: true }) as string[]) || [];

        return (
          <div key={cat} style={{ marginBottom: 8 }}>
            <button
              onClick={() => setOpenCats(prev => ({ ...prev, [cat]: !prev[cat] }))}
              aria-expanded={isOpen}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: isVela ? undefined : "#F3F4F6",
                border: isVela ? "2px solid transparent" : "none",
                backgroundImage: isVela ? "linear-gradient(white,white), linear-gradient(90deg,#046A38,#2E5FCE)" : undefined,
                backgroundOrigin: isVela ? "border-box" : undefined,
                backgroundClip: isVela ? "padding-box, border-box" : undefined,
                borderRadius: 16,
                padding: "18px 24px",
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#0E1A1A", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {CATEGORY_ICONS[cat]}
                </div>
                <div style={{ fontSize: 22, fontWeight: 500, color: "#0E1A1A" }}>{label}</div>
              </div>
              <svg
                width={16} height={16} viewBox="0 0 24 24"
                fill="none" stroke="#0E1A1A" strokeWidth={2.5}
                strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {isOpen && (
              <div style={{ marginTop: 8, background: "white", border: "1px solid #E5E7EB", borderRadius: 16, overflow: "hidden" }}>
                {rows.map((row, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "16px 24px",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      borderBottom: i === rows.length - 1 ? "none" : "1px solid #F3F4F6",
                    }}
                  >
                    <span style={{ color: "#046A38", fontWeight: 800, fontSize: 18 }}>✓</span>
                    <span style={{ fontSize: 14, color: "#0E1A1A", fontWeight: 500 }}>{row}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}